/**
 * Price Value Object
 * 
 * Domain concept: "What does this property cost?"
 * 
 * Business rules:
 * - Null/undefined prices are normalized to null (use hasValue to check)
 * - Zero is a valid price (some listings are $0, e.g., land leases)
 * 
 * Computed facts:
 * - hasValue: Whether a price exists (not null)
 * - isZero: Whether the price is exactly $0
 * - isPositive: Whether the price is greater than $0
 * 
 * Usage:
 * - Format: src/ui/price/format-price.ts
 * - UI: src/ui/price/hero-price.tsx
 * 
 * Examples:
 * - isPositive: Use to decide if price badge should show. A $0 listing
 *   shouldn't display "$0" prominently - show "Contact for Price" instead.
 * - isZero: Land leases, auction properties, or "price upon request" listings
 *   may have $0. Distinguish from null (no data) vs zero (intentional).
 * 
 * Note: This VO holds the raw numeric value. Currency and locale
 * formatting are handled by the format function, not here.
 */
export class Price {
  readonly #amount: number | null;

  private constructor(amount: number | null) {
    this.#amount = amount;
  }

  /**
   * Creates a Price from a numeric value.
   * Always returns an instance - use hasValue to check validity.
   * 
   * @param amount - The price amount (null/undefined normalized to null)
   */
  static from(amount: number | null | undefined): Price {
    return new Price(amount ?? null);
  }

  /**
   * The raw numeric price value.
   */
  get value(): number | null {
    return this.#amount;
  }

  /**
   * Whether a price exists (not null).
   * Use this before displaying or comparing prices.
   */
  get hasValue(): boolean {
    return this.#amount !== null;
  }

  /**
   * Whether the price is exactly zero.
   * Some listings (e.g., land leases) have $0 prices.
   */
  get isZero(): boolean {
    return this.#amount === 0;
  }

  /**
   * Whether the price is greater than zero.
   * Most common case for valid, displayable prices.
   */
  get isPositive(): boolean {
    return this.#amount !== null && this.#amount > 0;
  }
}
